import { Chunk, CHUNK_STATE, CHUNK_X, CHUNK_Z, WORLD_HEIGHT } from "./chunk";
import { SimplexNoise } from "./noise";
import { hash3, clamp, smoothstep } from "../core/rng";
import {
  AIR, BEDROCK, COAL_ORE, DIAMOND_ORE, DIRT, GOLD_ORE, GRAVEL, GRASS,
  IRON_ORE, LEAVES, LOG, SAND, SANDSTONE, SNOW, STONE, WATER,
} from "./blocks";

export const SEA_LEVEL = 46;
/** Above this height, mountain surfaces are capped with snow. */
export const SNOW_LINE = 92;

export const BIOME = {
  OCEAN: 0,
  BEACH: 1,
  PLAINS: 2,
  FOREST: 3,
  DESERT: 4,
  MOUNTAINS: 5,
  TUNDRA: 6,
} as const;
export type Biome = (typeof BIOME)[keyof typeof BIOME];

export const BIOME_NAMES: Record<Biome, string> = {
  [BIOME.OCEAN]: "Ocean",
  [BIOME.BEACH]: "Beach",
  [BIOME.PLAINS]: "Plains",
  [BIOME.FOREST]: "Forest",
  [BIOME.DESERT]: "Desert",
  [BIOME.MOUNTAINS]: "Mountains",
  [BIOME.TUNDRA]: "Tundra",
};

export interface ColumnInfo {
  height: number;
  biome: Biome;
  temperature: number;
  humidity: number;
}

/**
 * Stateless terrain generator. Given the same seed it always produces the same
 * world, because every decision derives from noise or positional hashing rather
 * than iteration order.
 */
export class TerrainGenerator {
  readonly seed: number;

  private readonly nContinent: SimplexNoise;
  private readonly nHill: SimplexNoise;
  private readonly nRidge: SimplexNoise;
  private readonly nTemperature: SimplexNoise;
  private readonly nHumidity: SimplexNoise;
  private readonly nCave: SimplexNoise;
  private readonly nTunnel: SimplexNoise;
  private readonly nOre: SimplexNoise;

  constructor(seed: number) {
    this.seed = seed;
    this.nContinent = new SimplexNoise(seed);
    this.nHill = new SimplexNoise(seed + 1013);
    this.nRidge = new SimplexNoise(seed + 2027);
    this.nTemperature = new SimplexNoise(seed + 3041);
    this.nHumidity = new SimplexNoise(seed + 4057);
    this.nCave = new SimplexNoise(seed + 5077);
    this.nTunnel = new SimplexNoise(seed + 6089);
    this.nOre = new SimplexNoise(seed + 7103);
  }

  /** Full column description — useful for the HUD and for spawn picking. */
  columnAt(wx: number, wz: number): ColumnInfo {
    const cont = this.nContinent.fbm2(wx * 0.0009, wz * 0.0009, 4);
    const hill = this.nHill.fbm2(wx * 0.013, wz * 0.013, 4);
    const ridgeRaw = this.nRidge.fbm2(wx * 0.0035, wz * 0.0035, 3);
    const ridge = 1 - Math.abs(ridgeRaw);

    // fbm output is concentrated near zero, so map it through the *observed*
    // range rather than the theoretical [-1, 1] to get a usable land fraction.
    const cont01 = clamp((cont + 0.62) / 1.24, 0, 1);
    const isLand = smoothstep(0.40, 0.56, cont01);
    // Mountains only appear well inland.
    const mountainness = smoothstep(0.58, 0.88, cont01);

    // Coastline: deep ocean floor blends up onto low-lying plains. Getting this
    // blend wrong is what makes a world read as "all water".
    const seafloor = SEA_LEVEL - 16;
    const lowland = SEA_LEVEL + 3 + hill * 7;
    let height = seafloor + (lowland - seafloor) * isLand;
    height += ridge * ridge * 52 * mountainness;

    const temperature = this.nTemperature.fbm2((wx + 12000) * 0.00055, (wz - 6000) * 0.00055, 3);
    const humidity = this.nHumidity.fbm2((wx - 4000) * 0.00065, (wz + 9000) * 0.00065, 3);

    return {
      height,
      biome: this.biomeFor(height, temperature, humidity, mountainness),
      temperature,
      humidity,
    };
  }

  private biomeFor(height: number, temperature: number, humidity: number, mountainness: number): Biome {
    if (height < SEA_LEVEL - 1) return BIOME.OCEAN;
    if (height <= SEA_LEVEL + 1) return BIOME.BEACH;
    if (height > SEA_LEVEL + 34 && mountainness > 0.45) return BIOME.MOUNTAINS;
    if (temperature < -0.32) return BIOME.TUNDRA;
    if (temperature > 0.22 && humidity < 0.05) return BIOME.DESERT;
    if (humidity > 0.12) return BIOME.FOREST;
    return BIOME.PLAINS;
  }

  generate(chunk: Chunk): void {
    const { originX, originZ, blocks } = chunk;

    for (let lz = 0; lz < CHUNK_Z; lz++) {
      const wz = originZ + lz;
      for (let lx = 0; lx < CHUNK_X; lx++) {
        const wx = originX + lx;
        this.generateColumn(blocks, wx, wz, lx, lz);
      }
    }

    this.decorate(chunk);
    chunk.recomputeHeights();
    chunk.state = CHUNK_STATE.GENERATED;
    chunk.dirty = true;
  }

  private generateColumn(
    blocks: Uint8Array,
    wx: number,
    wz: number,
    lx: number,
    lz: number,
  ): void {
    const { height, biome } = this.columnAt(wx, wz);
    const surface = clamp(Math.floor(height), 1, WORLD_HEIGHT - 2);

    for (let y = 0; y <= surface; y++) {
      blocks[lx + CHUNK_X * lz + CHUNK_X * CHUNK_Z * y] = this.blockFor(biome, y, surface, wx, wz);
    }

    // Fill any air pockets between the terrain surface and sea level with water.
    for (let y = surface + 1; y <= SEA_LEVEL; y++) {
      const i = lx + CHUNK_X * lz + CHUNK_X * CHUNK_Z * y;
      if (blocks[i] === AIR) blocks[i] = WATER;
    }

    this.carveCaves(blocks, wx, wz, lx, lz, surface);
  }

  private blockFor(biome: Biome, y: number, surface: number, wx: number, wz: number): number {
    const depth = surface - y;
    if (y <= 0) return BEDROCK;
    if (y === 1 && hash3(wx, y, wz, this.seed) < 0.6) return BEDROCK;

    switch (biome) {
      case BIOME.OCEAN:
        if (y === surface) return hash3(wx, y, wz, this.seed + 991) < 0.25 ? GRAVEL : SAND;
        return depth < 3 ? SAND : this.ore(wx, y, wz);
      case BIOME.BEACH:
        return depth < 4 ? SAND : this.ore(wx, y, wz);
      case BIOME.DESERT:
        if (depth < 5) return SAND;
        if (depth < 9) return SANDSTONE;
        return this.ore(wx, y, wz);
      case BIOME.MOUNTAINS:
        // Rocky slopes with a snow line, so peaks read as mountains rather than
        // a solid white wall.
        if (depth === 0) return y > SNOW_LINE ? SNOW : STONE;
        return depth < 4 ? STONE : this.ore(wx, y, wz);
      case BIOME.TUNDRA:
        if (depth === 0) return SNOW;
        return depth < 4 ? DIRT : this.ore(wx, y, wz);
      case BIOME.PLAINS:
      case BIOME.FOREST:
      default:
        if (depth === 0) return GRASS;
        return depth < 4 ? DIRT : this.ore(wx, y, wz);
    }
  }

  /**
   * Pick an ore for a stone block. A single 3D field sampled at world
   * coordinates (rather than depth alone) produces compact blobs instead of the
   * horizontal bands you get from a depth-only function, and the depth gates
   * keep rarer ores out of the shallow crust.
   */
  private ore(wx: number, y: number, wz: number): number {
    const n = this.nOre.noise3D(wx * 0.085, y * 0.075, wz * 0.085);
    if (y < 20 && n > 0.7) return DIAMOND_ORE;
    if (y < 38 && n > 0.64) return GOLD_ORE;
    if (y < 54 && n > 0.58) return IRON_ORE;
    if (y < 70 && n > 0.52) return COAL_ORE;
    return STONE;
  }

  private carveCaves(blocks: Uint8Array, wx: number, wz: number, lx: number, lz: number, surface: number): void {
    // Two independent systems: cheese caverns and ridged tunnels.
    for (let y = 2; y < surface - 2; y++) {
      const cheese = this.nCave.fbm3(wx * 0.022, y * 0.038, wz * 0.022, 3);
      const tunnel = 1 - Math.abs(this.nTunnel.fbm3(wx * 0.014, y * 0.02, wz * 0.014, 2));

      const isCheese = Math.abs(cheese) < 0.055;
      const isTunnel = tunnel > 0.93 && y < surface - 6;
      if (!isCheese && !isTunnel) continue;

      const i = lx + CHUNK_X * lz + CHUNK_X * CHUNK_Z * y;
      const existing = blocks[i];
      // Never dissolve bedrock.
      if (existing === BEDROCK) continue;
      // Don't breach the sea floor into the ocean from below.
      if (y > SEA_LEVEL - 4 && isTunnel) continue;
      blocks[i] = AIR;
    }
  }

  /**
   * Trees are placed on a jittered grid. Each chunk also visits the cells just
   * outside its border, because a canopy reaches two blocks past its trunk and
   * a tree rooted next door still contributes leaves inside this chunk.
   */
  private decorate(chunk: Chunk): void {
    const CELL = 5;
    const { originX, originZ } = chunk;

    const cellMinX = Math.floor(originX / CELL) - 1;
    const cellMaxX = Math.floor((originX + CHUNK_X - 1) / CELL) + 1;
    const cellMinZ = Math.floor(originZ / CELL) - 1;
    const cellMaxZ = Math.floor((originZ + CHUNK_Z - 1) / CELL) + 1;

    for (let cz = cellMinZ; cz <= cellMaxZ; cz++) {
      for (let cx = cellMinX; cx <= cellMaxX; cx++) {
        const r1 = hash3(cx, 0, cz, this.seed + 7717);
        if (r1 > 0.55) continue;

        const tx = cx * CELL + Math.floor(hash3(cx, 1, cz, this.seed) * CELL);
        const tz = cz * CELL + Math.floor(hash3(cx, 2, cz, this.seed) * CELL);

        const lx = tx - originX;
        const lz = tz - originZ;
        const { height, biome } = this.columnAt(tx, tz);
        if (height <= SEA_LEVEL + 1) continue;

        const density = biome === BIOME.FOREST ? 0.75 : biome === BIOME.PLAINS ? 0.3 : 0;
        if (hash3(cx, 3, cz, this.seed + 331) > density) continue;

        // A trunk outside this chunk only contributes canopy here, so the
        // ground check applies only when the trunk actually lands in the chunk.
        if (lx >= 0 && lx < CHUNK_X && lz >= 0 && lz < CHUNK_Z) {
          const ground = chunk.get(lx, Math.floor(height), lz);
          if (ground !== GRASS) continue;
        }

        this.placeTree(chunk, lx, Math.floor(height) + 1, lz, hash3(cx, 4, cz, this.seed) * 2 | 0);
      }
    }
  }

  private placeTree(chunk: Chunk, lx: number, ly: number, lz: number, variant: number): void {
    const height = 4 + (variant % 3);
    if (ly + height + 2 >= WORLD_HEIGHT - 1) return;

    if (lx >= 0 && lx < CHUNK_X && lz >= 0 && lz < CHUNK_Z) {
      for (let i = 0; i < height; i++) {
        if (ly + i > 0 && ly + i < WORLD_HEIGHT) chunk.set(lx, ly + i, lz, LOG);
      }
    }

    // Canopy: two wide layers, then a narrower cap.
    const topY = ly + height - 1;
    for (let dy = -2; dy <= 1; dy++) {
      const radius = dy <= -1 ? 2 : 1;
      for (let dz = -radius; dz <= radius; dz++) {
        for (let dx = -radius; dx <= radius; dx++) {
          if (dx === 0 && dz === 0 && dy <= 0) continue;
          // Trim corners for a rounder silhouette.
          if (radius === 2 && Math.abs(dx) === 2 && Math.abs(dz) === 2) continue;
          const x = lx + dx;
          const y = topY + dy;
          const z = lz + dz;
          if (x < 0 || x >= CHUNK_X || z < 0 || z >= CHUNK_Z || y < 0 || y >= WORLD_HEIGHT) continue;
          if (chunk.get(x, y, z) === AIR) chunk.set(x, y, z, LEAVES);
        }
      }
    }
  }
}
